import { useCallback, useMemo, useRef, useState } from 'react';
import NudosSearchbarDropdown from 'components/NudosComponents/NudosSearchbarDropdown/NudosSearchbarDropdown';
import { NudosHoverText, NudosSelectButton } from 'components/NudosComponents';
import { IKamClient } from 'types/kamClient.types';
import { IorganizationBusinessData } from 'types/bussines';
import { addKamToOrganization, removeKamFromOrganization } from 'services/kamServices';
import {
  displayErrorNotification,
  displaySuccessNotification
} from 'utils/displayNudosStandardNotifications';

import './OrganizationKamAssignments.scss';

interface IOrganizationKamAssignmentsProps {
  organizationId: string;
  organizationData: IorganizationBusinessData;
  updateWithoutLoaders?: () => Promise<void>;
}

const OrganizationKamAssignments = ({
  organizationId,
  organizationData,
  updateWithoutLoaders
}: IOrganizationKamAssignmentsProps) => {
  const [loadingAction, setLoadingAction] = useState<boolean>(false);
  const loadingActionRef = useRef<boolean>(false);
  const kamDropdownPlaceholder = 'Selecciona el KAM';

  const assignedKams = useMemo(
    () =>
      organizationData?.assignedKams ??
      (organizationData?.assignedKam ? [organizationData.assignedKam] : []),
    [organizationData]
  );

  const hasSingleAssignedKam = assignedKams.length === 1;
  const dropdownCurrentValue = hasSingleAssignedKam ? assignedKams[0] : undefined;

  const assignedKamIds = useMemo(
    () =>
      new Set(
        assignedKams
          .map(kam => Number(kam?.id))
          .filter(kamId => Number.isFinite(kamId))
      ),
    [assignedKams]
  );

  const kamOptions = useMemo(() => {
    const optionsById = new Map<number, IKamClient>();

    (organizationData?.availableKams ?? []).forEach(kam => {
      const kamId = Number(kam?.id);
      if (Number.isFinite(kamId)) {
        optionsById.set(kamId, kam);
      }
    });

    assignedKams.forEach(kam => {
      const kamId = Number(kam?.id);
      if (Number.isFinite(kamId)) {
        optionsById.set(kamId, kam);
      }
    });

    return Array.from(optionsById.values());
  }, [assignedKams, organizationData?.availableKams]);

  const closedDropdownPlaceholder = useMemo(() => {
    if (!assignedKams.length) {
      return kamDropdownPlaceholder;
    }

    if (assignedKams.length === 1) {
      return assignedKams[0]?.email ?? kamDropdownPlaceholder;
    }

    return `${assignedKams.length} KAMs seleccionados`;
  }, [assignedKams]);

  const kamAssignmentsDropdownKey = useMemo(
    () =>
      assignedKams
        .map(kam => Number(kam?.id))
        .filter(kamId => Number.isFinite(kamId))
        .sort((firstId, secondId) => firstId - secondId)
        .join('-') || 'no-kams',
    [assignedKams]
  );

  const refreshOrganizationData = useCallback(async () => {
    if (updateWithoutLoaders) {
      await updateWithoutLoaders();
    }
  }, [updateWithoutLoaders]);

  const handleToggleKam = useCallback(
    async (kam: IKamClient) => {
      const kamUserId = Number(kam?.id);

      if (!Number.isFinite(kamUserId) || loadingActionRef.current) {
        return;
      }

      const isAssigned = assignedKamIds.has(kamUserId);
      loadingActionRef.current = true;
      setLoadingAction(true);

      try {
        if (isAssigned) {
          await removeKamFromOrganization({
            organizationId: Number(organizationId),
            kamUserId
          });
          displaySuccessNotification({ customJSXMessage: <>KAM eliminado correctamente</> });
        } else {
          await addKamToOrganization({
            organizationId: Number(organizationId),
            kamUserId
          });
          displaySuccessNotification({ customJSXMessage: <>KAM agregado correctamente</> });
        }

        await refreshOrganizationData();
      } catch {
        displayErrorNotification();
      } finally {
        loadingActionRef.current = false;
        setLoadingAction(false);
      }
    },
    [assignedKamIds, organizationId, refreshOrganizationData]
  );

  const filterBySearchStringCallback = useCallback((search: string, rawOptions: IKamClient[]) => {
    return rawOptions.filter(kam =>
      kam?.email?.toLowerCase().trim().includes(search.toLowerCase().trim())
    );
  }, []);

  const formDropdownOptionsCallback = useCallback(
    (rawOptions: IKamClient[]) => {
      if (!rawOptions.length) {
        return [
          <div className="optionContainer" key="kam-options-empty">
            No hay KAMs disponibles
          </div>
        ];
      }

      return rawOptions.map(kam => {
        const kamId = Number(kam?.id);
        const kamKey = `kam-option-${kamId}`;
        const isAssigned = assignedKamIds.has(kamId);

        return (
          <div
            key={kamKey}
            className="optionContainer organizationKamAssignmentsOption"
            onClick={() => handleToggleKam(kam)}
          >
            <NudosHoverText
              onlyIfTruncated
              customClassName={!kam?.email ? 'empty' : undefined}
              text={kam?.email || ''}
              charactersLimit={50}
            />
            <NudosSelectButton
              iconName="xmark"
              componentSize="small"
              isButtonActive={isAssigned}
              colorPalette="orange"
            />
          </div>
        );
      });
    },
    [assignedKamIds, handleToggleKam]
  );

  const formSelectedOptionUICallback = useCallback(
    (kam: IKamClient) => <span className="organizationKamAssignmentsSelectedValue">{kam?.email}</span>,
    []
  );

  return (
    <div className="organizationKamAssignments">
      <div className="accountTitle">KAM del cliente</div>
      <NudosSearchbarDropdown
        key={kamAssignmentsDropdownKey}
        customClassname="organizationKamAssignmentsDropdown"
        componentSize="large"
        currentValue={dropdownCurrentValue}
        customPlaceholder={closedDropdownPlaceholder}
        isFilled={assignedKams.length > 0}
        rawOptions={kamOptions}
        includeMagnifierIcon={false}
        closesOnChangeSelection={false}
        showSkeletonLoader={loadingAction}
        formDropdownOptionsCallback={formDropdownOptionsCallback}
        formSelectedOptionUICallback={formSelectedOptionUICallback}
        filterBySearchStringCallback={filterBySearchStringCallback}
        customInputClass="organizationKamAssignmentsSearchInput"
      />
    </div>
  );
};

export default OrganizationKamAssignments;
